import React, { useRef, type RefObject } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { heroSocials } from '../data/heroStats';
import { closingFooter } from '../data/closingFooter';
import { parentCompany } from '../data/parentCompany';
import { focusInView } from '../utils/reveal';
import { QuestDragReveal } from './QuestDragReveal';
import { useCurrentYear } from '../hooks/useCurrentYear';

/**
 * Footer-row blend-in. This row sits at the very end of the page, so a strict "fully on screen and above
 * the bottom edge" trigger could never be met in some browsers/window sizes (the page can't scroll any
 * further) — the items stayed invisible. A partial trigger always fires once the row is reached.
 */
const ROW = {
  y: 12,
  blur: 12,
  amount: 0.2
};
const ROW_STAGGER = 0.08;
const LINK = 'rounded-sm transition-opacity duration-200 ease-out hover:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

/**
 * Closing section / footer: tagline + socials on top, the hero statement with an email pill, the giant
 * Pixel (who holds the middle of the screen here), and a quiet footer row.
 * Uses the hero's exact type styles; everything comes into focus as it scrolls into view.
 */
interface ClosingHeroProps {
  /** Pixel's last spot, dead centre of the footer — he jumps here from the corner once it's half in view */
  slotRef: RefObject<HTMLDivElement>;
}
export function ClosingHero({
  slotRef
}: ClosingHeroProps) {
  const reduce = useReducedMotion();
  const year = useCurrentYear();
  // All caps, opened up slightly — the tight sentence-case tracking crowds capitals
  const small = 'text-[clamp(0.875rem,calc(1.05*var(--vw)),1.25rem)] font-bold uppercase leading-tight tracking-[0.01em]';
  const footerRef = useRef<HTMLElement>(null);
  // From the footer's top entering the screen until it fully fills it (the page end)
  const {
    scrollYProgress
  } = useScroll({
    target: footerRef,
    offset: ['start end', 'end end']
  });
  const unveilY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['-60%', '0%']);
  return <footer id="contact" ref={footerRef} aria-label="Contact" // Height follows the width like the reference (~0.62 × width), never taller than the screen
  // Phones: grows with its stacked content; desktop: fixed height like the hero
  // Phones: exactly one screen tall
  // Desktop: at least 80% of the window (min 600px) but free to grow — a fixed height clipped the
  // bottom row (links, address, legal) in shorter windows or with larger browser text
  className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-black text-white md:h-auto md:min-h-[max(80vh,600px)]" data-id="element-14" data-mp="1lz6y2dor8u10">
      {/* Unveil: the content starts pushed up behind the work images above and lags the scroll heavily,
          settling into place only as the footer fully arrives — so the images seem to lift off it */}
      {/* Phones: a two-column grid, mostly full-width rows, in order — Pixel (centred ~40% down the screen),
          Let's talk + email, page links, socials, address, copyright, then logo left / legal right
          (the row wrappers dissolve with `contents`) */}
      <motion.div
    // Phones: the first row (Pixel's spot) takes whatever height is left once the content below fits
    className="relative grid h-full grid-cols-2 grid-rows-[auto_minmax(0,1fr)] gap-x-4 gap-y-6 px-3 md:flex md:h-auto md:min-h-[max(80vh,600px)] md:flex-col pb-[calc(1.25rem+env(safe-area-inset-bottom,0px))] md:pb-[clamp(2.5rem,calc(4.5*var(--vw)),4.5rem)] pt-28 md:pt-[clamp(3rem,calc(6*var(--vw)),6rem)] will-change-transform md:gap-0 md:px-[calc(1.6*var(--vw))]" style={{
      y: unveilY
    }} data-id="element-15" data-mp="3p88nv02urwde">
      {/* Top row: statement + email pill left, socials right — the statement's first line shares the socials' top edge */}
      <div className="contents md:relative md:z-30 md:flex md:flex-wrap md:items-start md:justify-between md:gap-x-6 md:gap-y-4" data-id="element-16" data-mp="14otavo2b7gip">
      {/* Phones: first, in the top-left corner */}
      <div className="relative z-30 order-1 col-span-2 md:order-1" data-id="element-17" data-mp="tb10xjjsvwdy">
        {/* Same type as "Most brands chase attention. We build meaning." */}
        {/* Quest: the last shard hides behind this headline (drag it aside) */}
        <QuestDragReveal spot="footer" className="w-fit" data-id="element-18" data-mp="k9jocb5lqcu0">
        <p className="text-[clamp(2.5rem,calc(4.85*var(--vw)),6.5rem)] font-semibold leading-[1.02] tracking-[-0.045em]" data-id="element-19" data-mp="17ym41dgodvlv">
          {closingFooter.title.map((line, li) => <motion.span key={line} className="block w-fit" {...focusInView(0.15 + li * 0.15, reduce, {
                y: 14,
                blur: 14,
                amount: 1
              })} data-id="element-20" data-mp="y92oohs68jvr" data-mpi={li}>
              {line}
            </motion.span>)}
        </p>
        </QuestDragReveal>
        {/* Same button as the hero nav (size, type, hover fade on the wrapper); inverted to white for the black footer */}
        <div className="mt-[clamp(1rem,calc(1.6*var(--vw)),1.75rem)] w-fit transition-opacity duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-within:opacity-[0.7] [@media(hover:hover)]:hover:opacity-[0.7] motion-reduce:transition-none" data-id="element-21" data-mp="1vpzwdjettr6u">
          <motion.a href={`mailto:${closingFooter.email}`} className="inline-flex h-[clamp(2.5rem,calc(3.2*var(--vw)),3rem)] items-center whitespace-nowrap rounded-full bg-white px-[clamp(1.25rem,calc(1.8*var(--vw)),1.75rem)] text-[clamp(0.875rem,calc(1.15*var(--vw)),1.05rem)] font-semibold leading-[1.2] tracking-[-0.03em] text-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" aria-label={`${closingFooter.cta}: ${closingFooter.email}`} {...focusInView(0.45, reduce, {
              y: 12,
              blur: 12,
              amount: 1
            })} data-id="element-22" data-mp="2essc75f2bifr">
            {closingFooter.cta}
          </motion.a>
        </div>
        {/* Parent company, right beneath: same size, outlined in white so the email stays the main action */}
        <div className="mt-2 w-fit transition-opacity duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-within:opacity-[0.7] [@media(hover:hover)]:hover:opacity-[0.7] motion-reduce:transition-none" data-id="element-23" data-mp="3ajlw3b9g7pxd">
          <motion.a href={parentCompany.href} target="_blank" rel="noopener noreferrer" className="inline-flex h-[clamp(2.5rem,calc(3.2*var(--vw)),3rem)] items-center whitespace-nowrap rounded-full border-2 border-white px-[clamp(1.25rem,calc(1.8*var(--vw)),1.75rem)] text-[clamp(0.875rem,calc(1.15*var(--vw)),1.05rem)] font-semibold leading-[1.2] tracking-[-0.03em] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" {...focusInView(0.55, reduce, {
              y: 12,
              blur: 12,
              amount: 1
            })} data-id="element-24" data-mp="3jka33m0fcq0r">
            {parentCompany.label}
          </motion.a>
        </div>
      </div>
        <motion.nav aria-label="Social" className={`relative z-30 order-4 col-span-2 mt-2 w-full md:order-2 md:mt-0 md:w-auto md:text-right ${small}`} {...focusInView(0.1, reduce, {
          y: 12,
          blur: 12,
          amount: 1
        })} data-id="element-25" data-mp="2d3ogi1s7h91l">
          {heroSocials.map((link, i) => <React.Fragment key={link.label} data-id="element-26" data-mp="20m42cc89hq16" data-mpi={i}>
              {i > 0 && <span aria-hidden="true" className="px-[0.6em] text-neutral-500" data-id="element-27" data-mp="1vky45yvu1ak9">
                  /
                </span>}
              <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-28" data-mp="1qpirha9k9kpf">
                {link.label}
              </a>
            </React.Fragment>)}
        </motion.nav>
      </div>


      {/* Pixel's spot: centre of the footer, hero-sized (not a dock spot, so he stays here) */}
      <div ref={slotRef} aria-hidden="true" data-drone-keep-scale="true" data-drone-footer="true"
      // Phones: dead centre of the footer (= the screen), a bit smaller
      // (a touch smaller, so he clears the two-line headline + buttons above and the links below)
      data-drone-mobile-scale="0.68"
      // Phones: dead centre of the footer (it's exactly one screen tall, so the middle of the screen)
      className="pointer-events-none absolute inset-x-0 top-1/2 h-[40svh] w-full -translate-y-1/2 md:absolute md:inset-x-0 md:top-1/2 md:order-none md:h-[46vh] md:min-h-[360px] md:-translate-y-1/2" data-id="element-29" data-mp="17wolh5tx12xc" />

      {/* Giant faint wordmark behind Pixel, inverted to white — desktop: edge to edge, level with him */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 z-10 hidden -translate-y-1/2 px-[1.28vw] md:block" aria-hidden="true" data-id="element-30" data-mp="240vud51627ca">
        {/* The white tint (invert) lives on a plain wrapper: the blur-in animates `filter` on the image and
            clears it when done, which used to wipe out the invert and leave a black logo on black */}
        {/* Wide wordmark: edge to edge, level with Pixel */}
        <div className="opacity-[0.08] invert" data-id="element-31" data-mp="1l5ovvti4zwsw">
          <motion.img src="https://cdn.magicpatterns.com/uploads/c59J3HuuyLAaZJUH7snjBF/Vector_(3).svg" alt="" className="block h-auto w-full" {...focusInView(0, reduce, {
            y: 0,
            blur: 16,
            duration: 1.8,
            amount: 0.4
          })} data-id="element-32" data-mp="1p3uz4rjrnu92" />
        </div>
      </div>
      {/* Phones: vertical like the hero, running the full height of the footer */}
      <img src="https://cdn.magicpatterns.com/uploads/c59J3HuuyLAaZJUH7snjBF/Vector_(3).svg" alt="" aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 z-10 block h-auto w-[calc(max(100svh,560px)-16px)] max-w-none -translate-x-1/2 -translate-y-1/2 rotate-90 opacity-[0.06] invert md:hidden" data-id="element-33" data-mp="1ahpic8mzo9hr" />

      {/* Phones: empty row that pushes the left-hand footer items down to the bottom */}
      <div aria-hidden="true" className="order-2 col-span-2 md:hidden" data-id="element-34" data-mp="1u6zy1mmqiufv" />

      {/* Bottom footer row */}
      {/* Each column blends in on its own, left to right, once the row has actually been uncovered */}
      <div className={`contents md:relative md:z-30 md:mt-auto md:grid md:grid-cols-[3.6fr_1fr_auto] md:items-end md:gap-x-4 md:gap-y-8 ${small}`} data-id="element-35" data-mp="234s7491v55ut">
        {/* Desktop: first column stacks the logo on top with the legal notice beneath it, both left-aligned
            (phones: the wrapper dissolves and both keep their own spots) */}
        <div className="contents md:flex md:flex-col md:items-start md:gap-[clamp(1.25rem,calc(2*var(--vw)),2rem)]" data-id="element-36" data-mp="kyjgadcsa8im">
        <motion.a href="#" aria-label="Back to top" // Phones: left column, between the socials and the copyright (the rows above shift up to make room)
          className={`${LINK} relative z-30 order-5 col-span-2 mt-4 w-fit md:order-none md:mt-0 md:col-span-1`} {...focusInView(0, reduce, ROW)} data-id="element-37" data-mp="1hn8zc705d766">
          <img src="https://cdn.magicpatterns.com/uploads/judzNcXwimifLQYqjxq8CM/Vector_(5).svg" alt=""
            // Black stacked logo inverted to white for the dark footer
            className="h-[2rem] w-auto invert md:h-[clamp(2rem,calc(2.8*var(--vw)),2.75rem)]" data-id="element-38" data-mp="13ng17qmajoo6" />
        </motion.a>
        {/* Legal notice: quieter than the rest */}
        <motion.p
          // Phones: narrower, so it never runs under the legal links on the right
          className={`relative z-30 order-6 col-span-2 max-w-[58%] text-white/30 md:order-none md:col-span-1 md:max-w-none ${small}`} {...focusInView(ROW_STAGGER, reduce, ROW)} data-id="element-39" data-mp="3j9mps7wutpol">
          {closingFooter.copyrightHolder} © {year} {closingFooter.copyrightSuffix}
        </motion.p>
        </div>
        <motion.nav aria-label="Footer" className={`relative z-30 order-3 col-span-2 md:order-none md:col-span-1 ${small}`} {...focusInView(ROW_STAGGER * 2, reduce, ROW)} data-id="element-40" data-mp="5u6x7myfl40b">
          <ul className="space-y-1.5" data-id="element-41" data-mp="ft600owim5fe">
            {closingFooter.links.map(l => <li key={l.label} data-id="element-42" data-mp="fqtey2vgney4">
                <a href={l.href} className={LINK} data-id="element-43" data-mp="2cm7r6s52hiny">
                  {l.label}
                </a>
              </li>)}
          </ul>
        </motion.nav>
        <motion.ul aria-label="Legal" // Phones: pinned to the bottom-right corner, under the logo
        className={`absolute bottom-[calc(1.25rem+env(safe-area-inset-bottom,0px))] right-3 z-30 space-y-1.5 text-right text-white/30 md:relative md:bottom-auto md:right-auto md:order-none ${small}`} {...focusInView(ROW_STAGGER * 3, reduce, ROW)} data-id="element-44" data-mp="2hmdkecqzjd85">
          {closingFooter.legal.map(l => <li key={l.label} data-id="element-45" data-mp="2elagymlpejpt">
              {/* Quiet links brighten on hover instead of fading further */}
              <Link to={l.href} className="rounded-sm transition-colors duration-200 ease-out hover:text-white focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-46" data-mp="2njm4gfr9x1e1">
                {l.label}
              </Link>
            </li>)}
        </motion.ul>
      </div>
      </motion.div>
    </footer>;
}